import { log } from '@/lib/logger';

export const BULK_EMAIL_DRAFT_STORAGE_PREFIX = 'nynch:bulk-email-drafts:v1:';

/** Drop drafts older than this so month-old work never resurfaces. */
export const BULK_EMAIL_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface StoredBulkEmailDraft {
  contactId: string;
  approvedRecipientEmail?: string;
  subject: string;
  body: string;
  scheduledFor?: string;
  generatedByAi?: boolean;
  generationLogId?: string | null;
  aiOriginalSubject?: string;
  aiOriginalBody?: string;
  sharedContent?: boolean;
  submitted?: boolean;
}

// Session recovery is independent of browser storage. A tenant change drops
// the entire cache; late cleanup from the previous tenant cannot repopulate it.
let sessionScope: string | null = null;
const sessionDrafts = new Map<string, BulkEmailDraftSnapshot>();
const sessionContactDrafts = new Map<string, StoredBulkEmailDraft>();

export function activateBulkEmailDraftSession(scope: string | null): void {
  if (scope === sessionScope) return;
  sessionDrafts.clear();
  sessionContactDrafts.clear();
  sessionScope = scope;
}

export function retainBulkEmailDraftSession(snapshot: BulkEmailDraftSnapshot): void {
  if (snapshot.userId !== sessionScope) return;
  sessionDrafts.set(snapshot.recipientKey, structuredClone(snapshot));
  for (const draft of snapshot.drafts) sessionContactDrafts.set(draft.contactId, structuredClone(draft));
}

export function loadBulkEmailDraftSession(scope: string, contactIds: readonly string[]): BulkEmailDraftSnapshot | null {
  if (scope !== sessionScope) return null;
  const recipientKey = buildBulkEmailRecipientKey(contactIds);
  const snapshot = sessionDrafts.get(recipientKey);
  const drafts = contactIds.flatMap(id => {
    const draft = sessionContactDrafts.get(id);
    return draft ? [structuredClone(draft)] : [];
  });
  if (!drafts.length) return null;
  return { ...(snapshot ? structuredClone(snapshot) : { version: 1 as const,
    userId: scope, recipientKey, savedAt: Date.now(), appliedTemplateSources: [] }), drafts };
}

export function clearBulkEmailDraftSession(scope: string, contactIds: readonly string[]): void {
  if (scope !== sessionScope) return;
  sessionDrafts.delete(buildBulkEmailRecipientKey(contactIds));
  for (const id of contactIds) sessionContactDrafts.delete(id);
}

export interface StoredAppliedTemplateSource {
  subject: string;
  body: string;
  appliedSubject: string;
  appliedBody: string;
}

export interface BulkEmailDraftSnapshot {
  version: 1;
  userId: string;
  /** Full sorted recipient id list — used to verify the key hash on load. */
  recipientKey: string;
  savedAt: number;
  drafts: StoredBulkEmailDraft[];
  appliedTemplateSources: Array<[string, StoredAppliedTemplateSource]>;
  selectedTemplateName?: string;
  excludedContactIds?: string[];
  sharedDraft?: { subject: string; body: string };
  editingShared?: boolean;
}

/** Raw draft revisions owned by one submission, before merge fields/signatures. */
export interface BulkEmailSubmittedDrafts {
  scope: string;
  recipientIds: string[];
  drafts: StoredBulkEmailDraft[];
}

export function matchesSubmittedBulkEmailDraft(draft: StoredBulkEmailDraft, submitted: StoredBulkEmailDraft): boolean {
  return draft.contactId === submitted.contactId
    && draft.subject === submitted.subject && draft.body === submitted.body
    && draft.approvedRecipientEmail === submitted.approvedRecipientEmail
    && Boolean(draft.generatedByAi) === Boolean(submitted.generatedByAi)
    && (draft.generationLogId ?? null) === (submitted.generationLogId ?? null)
    && draft.aiOriginalSubject === submitted.aiOriginalSubject
    && draft.aiOriginalBody === submitted.aiOriginalBody
    && Boolean(draft.sharedContent) === Boolean(submitted.sharedContent);
}

/** Keep empty submitted rows so initial seeds cannot resurrect a delivered email. */
export function reconcileBulkEmailDraftSnapshot(
  snapshot: BulkEmailDraftSnapshot,
  submitted: BulkEmailSubmittedDrafts,
): BulkEmailDraftSnapshot {
  if (snapshot.userId !== submitted.scope) return snapshot;
  const revisions = new Map(submitted.drafts.map(draft => [draft.contactId, draft]));
  const clearedIds = new Set<string>();
  const drafts = snapshot.drafts.map(draft => {
    const revision = revisions.get(draft.contactId);
    if (!revision || !matchesSubmittedBulkEmailDraft(draft, revision)) return draft;
    clearedIds.add(draft.contactId);
    return { contactId: draft.contactId, approvedRecipientEmail: draft.approvedRecipientEmail,
      subject: '', body: '', sharedContent: false, generatedByAi: false, submitted: true };
  });
  if (!clearedIds.size) return snapshot;
  const hasSharedDrafts = drafts.some(draft => draft.sharedContent);
  return { ...snapshot, drafts,
    excludedContactIds: [...new Set([...(snapshot.excludedContactIds ?? []), ...clearedIds])],
    appliedTemplateSources: snapshot.appliedTemplateSources.filter(([id]) => !clearedIds.has(id)),
    sharedDraft: hasSharedDrafts ? snapshot.sharedDraft : { subject: '', body: '' },
    editingShared: hasSharedDrafts && snapshot.editingShared,
  };
}

/** Throws on any unverified write: the publication receipt must survive a failure. */
export function reconcileBulkEmailSubmittedDrafts(
  submitted: BulkEmailSubmittedDrafts,
  storage: BulkEmailDraftStorage = window.localStorage,
): void {
  if (!canEnumerateKeys(storage)) throw new Error('Saved email drafts could not be checked. Retry to safely finish recovery.');
  const keys = new Set<string>([buildBulkEmailDraftStorageKey(submitted.scope, buildBulkEmailRecipientKey(submitted.recipientIds))]);
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key?.startsWith(BULK_EMAIL_DRAFT_STORAGE_PREFIX)) keys.add(key);
  }
  for (const key of keys) {
    const raw = storage.getItem(key);
    if (!raw) continue;
    const snapshot = parseSnapshot(raw);
    if (!snapshot) continue;
    const reconciled = reconcileBulkEmailDraftSnapshot(snapshot, submitted);
    if (reconciled === snapshot) continue;
    const payload = JSON.stringify(reconciled);
    storage.setItem(key, payload);
    if (storage.getItem(key) !== payload) throw new Error('Your email batch is confirmed, but saved drafts could not be updated. Retry to safely finish recovery.');
  }
  if (sessionScope !== submitted.scope) return;
  for (const [key, snapshot] of sessionDrafts) {
    sessionDrafts.set(key, reconcileBulkEmailDraftSnapshot(snapshot, submitted));
  }
  for (const revision of submitted.drafts) {
    const draft = sessionContactDrafts.get(revision.contactId);
    if (draft && matchesSubmittedBulkEmailDraft(draft, revision)) {
      sessionContactDrafts.set(draft.contactId, { contactId: draft.contactId,
        approvedRecipientEmail: draft.approvedRecipientEmail, subject: '', body: '', sharedContent: false, generatedByAi: false, submitted: true });
    }
  }
}

/**
 * localStorage-compatible surface. `length` / `key` are required for sweeping
 * orphaned snapshots; without them save still works but cannot reclaim quota.
 */
export type BulkEmailDraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> & {
  readonly length?: number;
  key?: (index: number) => string | null;
};

function getDefaultStorage(): BulkEmailDraftStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // Private-mode or blocked storage.
    return null;
  }
}

/** Sorted, stable key for the recipient set — different sets never collide. */
export function buildBulkEmailRecipientKey(contactIds: readonly string[]): string {
  return [...contactIds].filter(Boolean).sort().join(',');
}

/**
 * Fixed-length fingerprint of the recipient set for use in localStorage keys.
 * The full recipientKey remains inside the snapshot for verification on load.
 */
export function hashBulkEmailRecipientKey(recipientKey: string): string {
  // Dual FNV-1a → 16 hex chars (64 bits). Collision fails closed via payload check.
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5 ^ 0x9e3779b9;
  for (let index = 0; index < recipientKey.length; index += 1) {
    const code = recipientKey.charCodeAt(index);
    h1 ^= code;
    h1 = Math.imul(h1, 0x01000193);
    h2 ^= code + index + 1;
    h2 = Math.imul(h2, 0x01000193);
  }
  return `${(h1 >>> 0).toString(16).padStart(8, '0')}${(h2 >>> 0).toString(16).padStart(8, '0')}`;
}

export function buildBulkEmailDraftStorageKey(userId: string, recipientKey: string): string {
  return `${BULK_EMAIL_DRAFT_STORAGE_PREFIX}${userId}:${hashBulkEmailRecipientKey(recipientKey)}`;
}

function isStoredDraft(value: unknown): value is StoredBulkEmailDraft {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.contactId === 'string'
    && typeof row.subject === 'string'
    && typeof row.body === 'string';
}

function isStoredAppliedSource(value: unknown): value is StoredAppliedTemplateSource {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.subject === 'string'
    && typeof row.body === 'string'
    && typeof row.appliedSubject === 'string'
    && typeof row.appliedBody === 'string';
}

function parseSnapshot(raw: string): BulkEmailDraftSnapshot | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const record = parsed as Record<string, unknown>;
    if (record.version !== 1) return null;
    if (typeof record.userId !== 'string' || record.userId.length === 0) return null;
    if (typeof record.recipientKey !== 'string') return null;
    if (typeof record.savedAt !== 'number' || !Number.isFinite(record.savedAt)) return null;
    if (!Array.isArray(record.drafts)) return null;

    const drafts = record.drafts.filter(isStoredDraft);
    if (drafts.length === 0) return null;

    const appliedTemplateSources: Array<[string, StoredAppliedTemplateSource]> = [];
    if (Array.isArray(record.appliedTemplateSources)) {
      for (const entry of record.appliedTemplateSources) {
        if (!Array.isArray(entry) || entry.length < 2) continue;
        const [contactId, source] = entry;
        if (typeof contactId !== 'string' || !isStoredAppliedSource(source)) continue;
        appliedTemplateSources.push([contactId, source]);
      }
    }

    return {
      version: 1,
      userId: record.userId,
      recipientKey: record.recipientKey,
      savedAt: record.savedAt,
      drafts,
      appliedTemplateSources,
      selectedTemplateName: typeof record.selectedTemplateName === 'string'
        ? record.selectedTemplateName
        : undefined,
      excludedContactIds: Array.isArray(record.excludedContactIds)
        ? record.excludedContactIds.filter((id): id is string => typeof id === 'string') : [],
      sharedDraft: record.sharedDraft && typeof record.sharedDraft === 'object'
        && typeof (record.sharedDraft as Record<string, unknown>).subject === 'string'
        && typeof (record.sharedDraft as Record<string, unknown>).body === 'string'
        ? record.sharedDraft as { subject: string; body: string } : undefined,
      editingShared: record.editingShared === true,
    };
  } catch {
    return null;
  }
}

function snapshotHasContent(snapshot: BulkEmailDraftSnapshot): boolean {
  if (snapshot.excludedContactIds?.length) return true;
  return snapshot.drafts.some((draft) => {
    if (draft.subject.trim().length > 0) return true;
    if (/<img\b/i.test(draft.body)) return true;
    const plain = draft.body.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return plain.length > 0;
  });
}

function canEnumerateKeys(storage: BulkEmailDraftStorage): storage is BulkEmailDraftStorage & {
  readonly length: number;
  key: (index: number) => string | null;
} {
  return typeof storage.length === 'number' && typeof storage.key === 'function';
}

/** Keys under this feature's prefix only — never touch other origin storage. */
export function listBulkEmailDraftStorageKeys(storage: BulkEmailDraftStorage): string[] {
  if (!canEnumerateKeys(storage)) return [];
  const keys: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (typeof key === 'string' && key.startsWith(BULK_EMAIL_DRAFT_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }
  } catch (error) {
    log.warn('[bulkEmailDraftStorage] Failed to list draft keys', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
  return keys;
}

interface FeatureEntry {
  key: string;
  savedAt: number;
  snapshot: BulkEmailDraftSnapshot | null;
}

function readFeatureEntries(storage: BulkEmailDraftStorage): FeatureEntry[] {
  const entries: FeatureEntry[] = [];
  for (const key of listBulkEmailDraftStorageKeys(storage)) {
    try {
      const raw = storage.getItem(key);
      if (!raw) {
        entries.push({ key, savedAt: 0, snapshot: null });
        continue;
      }
      const snapshot = parseSnapshot(raw);
      entries.push({
        key,
        savedAt: snapshot?.savedAt ?? 0,
        snapshot,
      });
    } catch {
      entries.push({ key, savedAt: 0, snapshot: null });
    }
  }
  return entries;
}

/**
 * Remove expired or unreadable snapshots under this feature's prefix.
 * Safe to call often — only touches keys with BULK_EMAIL_DRAFT_STORAGE_PREFIX.
 * Returns how many keys were removed.
 */
export function sweepExpiredBulkEmailDrafts(
  storage: BulkEmailDraftStorage | null | undefined,
  options: { now?: Date; maxAgeMs?: number } = {},
): number {
  if (!storage) return 0;
  const nowMs = (options.now ?? new Date()).getTime();
  const maxAge = options.maxAgeMs ?? BULK_EMAIL_DRAFT_MAX_AGE_MS;
  let removed = 0;

  for (const entry of readFeatureEntries(storage)) {
    const expired = !entry.snapshot || nowMs - entry.savedAt > maxAge;
    if (!expired) continue;
    try {
      storage.removeItem(entry.key);
      removed += 1;
    } catch {
      // Ignore individual cleanup failures.
    }
  }
  return removed;
}

/**
 * Evict oldest feature snapshots (by savedAt) to free quota.
 * Never touches keys outside this feature's prefix.
 * @param keepKey — do not remove the key we are about to rewrite.
 */
export function evictOldestBulkEmailDrafts(
  storage: BulkEmailDraftStorage | null | undefined,
  options: { keepKey?: string; limit?: number } = {},
): number {
  if (!storage) return 0;
  const keepKey = options.keepKey;
  const limit = options.limit ?? Number.POSITIVE_INFINITY;

  const candidates = readFeatureEntries(storage)
    .filter((entry) => entry.key !== keepKey)
    .sort((left, right) => left.savedAt - right.savedAt);

  let removed = 0;
  for (const entry of candidates) {
    if (removed >= limit) break;
    try {
      storage.removeItem(entry.key);
      removed += 1;
    } catch {
      // Ignore individual eviction failures.
    }
  }
  return removed;
}

function trySetItem(
  storage: BulkEmailDraftStorage,
  key: string,
  value: string,
): { ok: true } | { ok: false; error: unknown } {
  try {
    storage.setItem(key, value);
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * Load a matching draft snapshot for this user + recipient set.
 * Returns null when missing, expired, scoped to another user/set, or unreadable.
 * Also sweeps expired orphan snapshots under this feature's prefix.
 */
export function loadBulkEmailDrafts(
  userId: string | null | undefined,
  contactIds: readonly string[],
  options: {
    storage?: BulkEmailDraftStorage | null;
    now?: Date;
    maxAgeMs?: number;
  } = {},
): BulkEmailDraftSnapshot | null {
  if (!userId) return null;
  const recipientKey = buildBulkEmailRecipientKey(contactIds);
  if (!recipientKey) return null;

  const storage = options.storage === undefined ? getDefaultStorage() : options.storage;
  if (!storage) return null;

  const now = options.now ?? new Date();
  const maxAgeMs = options.maxAgeMs ?? BULK_EMAIL_DRAFT_MAX_AGE_MS;

  try {
    // Reclaim orphaned sets that will never be opened again.
    sweepExpiredBulkEmailDrafts(storage, { now, maxAgeMs });

    const storageKey = buildBulkEmailDraftStorageKey(userId, recipientKey);
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    const snapshot = parseSnapshot(raw);
    if (!snapshot) {
      try {
        storage.removeItem(storageKey);
      } catch {
        // Ignore.
      }
      return null;
    }
    // Trust the payload, not the key hash alone.
    if (snapshot.userId !== userId) return null;
    if (snapshot.recipientKey !== recipientKey) return null;

    const nowMs = now.getTime();
    if (nowMs - snapshot.savedAt > maxAgeMs) {
      try {
        storage.removeItem(storageKey);
      } catch {
        // Ignore cleanup failures.
      }
      return null;
    }

    if (!snapshotHasContent(snapshot)) return null;
    return snapshot;
  } catch (error) {
    log.warn('[bulkEmailDraftStorage] Failed to load drafts', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Persist drafts. Never throws — storage full / private mode returns false.
 * Empty snapshots are not written (and any existing key is cleared).
 * On quota failure: sweep expired, evict oldest feature keys, retry once.
 */
export function saveBulkEmailDrafts(
  userId: string | null | undefined,
  contactIds: readonly string[],
  drafts: StoredBulkEmailDraft[],
  appliedTemplateSources: Array<[string, StoredAppliedTemplateSource]> = [],
  options: {
    storage?: BulkEmailDraftStorage | null;
    now?: Date;
    selectedTemplateName?: string;
    excludedContactIds?: string[];
    sharedDraft?: { subject: string; body: string };
    editingShared?: boolean;
    maxAgeMs?: number;
  } = {},
): boolean {
  if (!userId) return false;
  const recipientKey = buildBulkEmailRecipientKey(contactIds);
  if (!recipientKey) return false;

  const storage = options.storage === undefined ? getDefaultStorage() : options.storage;
  if (!storage) return false;

  const now = options.now ?? new Date();
  const maxAgeMs = options.maxAgeMs ?? BULK_EMAIL_DRAFT_MAX_AGE_MS;

  const snapshot: BulkEmailDraftSnapshot = {
    version: 1,
    userId,
    recipientKey,
    savedAt: now.getTime(),
    drafts,
    appliedTemplateSources,
    selectedTemplateName: options.selectedTemplateName,
    excludedContactIds: options.excludedContactIds,
    sharedDraft: options.sharedDraft,
    editingShared: options.editingShared,
  };

  if (!snapshotHasContent(snapshot)) {
    clearBulkEmailDrafts(userId, contactIds, { storage });
    return false;
  }

  const storageKey = buildBulkEmailDraftStorageKey(userId, recipientKey);
  const payload = JSON.stringify(snapshot);

  // Opportunistic cleanup so orphan sets do not accumulate forever.
  sweepExpiredBulkEmailDrafts(storage, { now, maxAgeMs });

  const first = trySetItem(storage, storageKey, payload);
  if (first.ok) return true;

  // Quota / private-mode: free our own space and retry once, never throw.
  try {
    sweepExpiredBulkEmailDrafts(storage, { now, maxAgeMs });
    evictOldestBulkEmailDrafts(storage, { keepKey: storageKey });

    const retry = trySetItem(storage, storageKey, payload);
    if (retry.ok) return true;

    log.warn('[bulkEmailDraftStorage] Failed to save drafts after eviction', {
      error: retry.error instanceof Error ? retry.error.message : String(retry.error),
    });
    return false;
  } catch (error) {
    log.warn('[bulkEmailDraftStorage] Failed to save drafts', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

/** Remove the scoped draft snapshot after a successful queue or Start fresh. */
export function clearBulkEmailDrafts(
  userId: string | null | undefined,
  contactIds: readonly string[],
  options: { storage?: BulkEmailDraftStorage | null } = {},
): void {
  if (!userId) return;
  const recipientKey = buildBulkEmailRecipientKey(contactIds);
  if (!recipientKey) return;

  const storage = options.storage === undefined ? getDefaultStorage() : options.storage;
  if (!storage) return;

  try {
    storage.removeItem(buildBulkEmailDraftStorageKey(userId, recipientKey));
  } catch (error) {
    log.warn('[bulkEmailDraftStorage] Failed to clear drafts', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
